import { useMemo, useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  reorderWorkflowItemsMutationDocument,
  workflowRequestDocument,
} from '@liscio/api';
import { isWorkflowCustomStep, useGraphQLClient } from '@liscio/common';

/** Normalize section / request UUIDs for stable Map lookups across REST vs GraphQL. */
export function normalizeRequestSectionKey(id: string): string {
  return String(id).trim().toLowerCase();
}

export type FirmAuthoredContentPayload = {
  contentType?: string;
  textContent?: string;
  files?: Array<{
    id?: number;
    uuid?: string;
    name?: string;
    content_type?: string;
    contentType?: string;
  }>;
  title?: string;
  instructions?: string;
  required?: boolean;
};

/** Effective content kind for firm-authored options (handles snake_case + missing type when files exist). */
export function firmAuthoredEffectiveContentType(
  fac: FirmAuthoredContentPayload | null | undefined
): string {
  if (!fac) return 'text';
  const t = String(fac.contentType || '')
    .trim()
    .toLowerCase();
  if (t) return t;
  if (fac.files?.length) return 'file';
  return 'text';
}

/**
 * Normalize `options.firm_authored_content` from GraphQL JSON (Rails may use snake_case keys).
 */
export function normalizeFirmAuthoredContentFromOptions(
  raw: unknown
): FirmAuthoredContentPayload | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const ctRaw = r.contentType ?? r.content_type;
  let contentType =
    typeof ctRaw === 'string' ? ctRaw.trim().toLowerCase() : undefined;

  const textRaw = r.textContent ?? r.text_content;
  const textContent = typeof textRaw === 'string' ? textRaw : undefined;

  const filesRaw = r.files;
  const files = Array.isArray(filesRaw)
    ? filesRaw.map((entry) => {
        if (!entry || typeof entry !== 'object') {
          return {};
        }
        const f = entry as Record<string, unknown>;
        const idVal = f.id;
        const parsedId =
          typeof idVal === 'number'
            ? idVal
            : idVal != null && String(idVal).length > 0
              ? Number(idVal)
              : undefined;
        return {
          id:
            parsedId != null && Number.isFinite(parsedId)
              ? parsedId
              : undefined,
          uuid: typeof f.uuid === 'string' ? f.uuid : undefined,
          name: typeof f.name === 'string' ? f.name : undefined,
          content_type:
            (typeof f.content_type === 'string' ? f.content_type : undefined) ??
            (typeof f.contentType === 'string' ? f.contentType : undefined),
          contentType:
            typeof f.contentType === 'string' ? f.contentType : undefined,
        };
      })
    : undefined;

  if (!contentType && files?.length) {
    contentType = 'file';
  }

  return {
    contentType,
    textContent,
    files,
    title: typeof r.title === 'string' ? r.title : undefined,
    instructions: typeof r.instructions === 'string' ? r.instructions : undefined,
    required: typeof r.required === 'boolean' ? r.required : undefined,
  };
}

/** `options` JSON may use snake_case or camelCase depending on source. */
export function getFirmAuthoredContentRawFromOptions(
  options: unknown
): Record<string, unknown> | null {
  if (!options || typeof options !== 'object') return null;
  const o = options as Record<string, unknown>;
  const raw = o.firm_authored_content ?? o.firmAuthoredContent;
  if (raw && typeof raw === 'object') {
    return raw as Record<string, unknown>;
  }
  return null;
}

export function getClientActionFromOptions(
  options: unknown
): string | undefined {
  if (!options || typeof options !== 'object') return undefined;
  const o = options as Record<string, unknown>;
  const a = o.client_action ?? o.clientAction;
  return typeof a === 'string' ? a : undefined;
}

/**
 * When `createWorkflowItem2` set `client_action: approve` but sanitized FAC was blank
 * (e.g. key mismatch), preview still needs a minimal FAC so file custom steps (REST
 * `reviewable_document`) get the delete control like `text` steps.
 */
export function syntheticFirmAuthoredForApproveFallback(
  workflowItemType: string | null | undefined
): FirmAuthoredContentPayload | null {
  const t = String(workflowItemType || '');
  if (t.includes('WfiReviewableDocument')) {
    return { contentType: 'file' };
  }
  if (t.includes('WfiAnswerTextQuestion')) {
    return { contentType: 'text' };
  }
  if (t.includes('WfiFirmAuthoredTextAcknowledgement')) {
    return { contentType: 'text' };
  }
  return null;
}

const REST_ITEM_TYPE_TO_SYNTHETIC_WORKFLOW_TYPE: Record<string, string> = {
  text: 'WfiAnswerTextQuestion',
  firm_authored_text: 'WfiFirmAuthoredTextAcknowledgement',
  reviewable_document: 'WfiReviewableDocument',
};

/**
 * Derive `firm_authored_content` preview shape from `WorkflowItem#options` JSON
 * (same keys as GraphQL `options`). Used in tests; client preview reads options via GraphQL only.
 */
export function firmAuthoredContentFromRestWorkflowOptions(
  workflowItemOptions: unknown,
  restItemType: string | null | undefined
): FirmAuthoredContentPayload | null {
  const rawFacObj = getFirmAuthoredContentRawFromOptions(workflowItemOptions);
  const useSynthetic = isWorkflowCustomStep(workflowItemOptions);

  const wfType = restItemType
    ? REST_ITEM_TYPE_TO_SYNTHETIC_WORKFLOW_TYPE[restItemType]
    : undefined;

  const facSource: unknown =
    rawFacObj ??
    (useSynthetic && wfType
      ? syntheticFirmAuthoredForApproveFallback(wfType)
      : null);

  if (!facSource || typeof facSource !== 'object') {
    return null;
  }
  const normalized = normalizeFirmAuthoredContentFromOptions(facSource);
  return normalized ?? null;
}

/** Map firms REST document dependency to the shape `enrichFirmAuthoredContentWithDocumentDependency` expects. */
export function documentDependencyForFirmAuthoredEnrich(
  dependency: { type?: string | null; object?: unknown } | null | undefined
): { __typename: 'Document'; documentId: string; uuid: string } | null {
  if (!dependency || dependency.type !== 'document') return null;
  const obj = dependency.object as Record<string, unknown> | null | undefined;
  if (!obj || typeof obj !== 'object') return null;
  const uuid = typeof obj.uuid === 'string' ? obj.uuid : '';
  if (!uuid) return null;
  const idVal = obj.document_id ?? obj.documentId ?? obj.id;
  const documentId =
    idVal != null && String(idVal).length > 0 ? String(idVal) : '';
  return { __typename: 'Document', documentId, uuid };
}

/** GraphQL `WorkflowRequestItem.dependency` when resolved as `Document`. */
type DocumentDependencyFragment = {
  __typename?: 'Document';
  documentId: string;
  uuid: string;
};

/**
 * When the backend sets `WfRequestItem#liscio_object` but `options.files` is empty
 * or stale, still resolve a file row for preview (`get_document_details`).
 */
export function enrichFirmAuthoredContentWithDocumentDependency(
  fac: FirmAuthoredContentPayload,
  dependency: unknown
): FirmAuthoredContentPayload {
  const ct = firmAuthoredEffectiveContentType(fac);
  if (ct !== 'file' && ct !== 'image') {
    return fac;
  }
  if (fac.files?.length) {
    return fac;
  }
  if (!dependency || typeof dependency !== 'object') {
    return fac;
  }
  const dep = dependency as DocumentDependencyFragment;
  if (dep.__typename !== 'Document') {
    return fac;
  }
  const idRaw = dep.documentId;
  const idNum =
    idRaw != null && String(idRaw).length > 0 ? Number(idRaw) : NaN;
  const uuid = dep.uuid;
  if (Number.isNaN(idNum) && !uuid) {
    return fac;
  }
  return {
    ...fac,
    files: [
      {
        ...(Number.isNaN(idNum) ? {} : { id: idNum }),
        uuid: uuid || '',
        name: fac.title || '',
        content_type: '',
      },
    ],
  };
}

/** Narrowed shape until GraphQL codegen includes workflowSection + item options */
type RequestSectionRow = {
  id: string;
  title?: string | null;
  workflowSection?: { id: string; title?: string | null } | null;
};

type WorkflowItemRow = {
  id: string;
  type?: string | null;
  position?: number | null;
  options?: Record<string, unknown> | null;
  requestItem?: {
    id: string;
    dependency?: unknown;
  } | null;
};

/**
 * Represents the workflow-level section/item IDs needed for the reorder mutation.
 */
export type WorkflowSectionMapping = {
  workflowSectionId: string;
  workflowItemIds: string[];
};

/**
 * Hook that provides:
 * 1. A mapping from **REST / request section UUID** → workflow section + item IDs
 *    (joins GraphQL `requestSections` to template `workflow.sections` via
 *    `workflowSection.id`, avoiding index-only alignment bugs).
 * 2. A map of **request item UUID** → `firm_authored_content` from workflow item `options`,
 *    enriched from **`requestItem.dependency`** (Document) when `files` is missing.
 * 2b. Same payload keyed by **workflow item UUID** when GraphQL `requestItem` is missing
 *    or ordering is resolved by workflow item id (preview still resolves firm text/file metadata).
 * 3. A map of **request item UUID** → `true` when GraphQL `options.is_custom_step` applies.
 * 4. A mutation function to reorder workflow items within a section.
 */
export function useReorderPreviewItems(requestId: string, enabled: boolean) {
  const gqlClient = useGraphQLClient();
  const queryClient = useQueryClient();

  const { data: workflowRequestData, isLoading: isWorkflowLoading } =
    gqlClient.useGraphQLWithOptions(
      workflowRequestDocument,
      {
        enabled: enabled && Boolean(requestId),
        cacheTime: Infinity,
        staleTime: 30000,
      },
      { id: requestId }
    );

  const workflowId = useMemo(
    () => workflowRequestData?.workflowRequest?.workflow?.id || null,
    [workflowRequestData]
  );

  const { sectionMappingsByRequestSectionUuid, sectionMappingsByStepIndex } =
    useMemo(() => {
      const map = new Map<string, WorkflowSectionMapping>();
      const byStep: WorkflowSectionMapping[] = [];
      const workflow = workflowRequestData?.workflowRequest?.workflow;
      const requestSections = (workflowRequestData?.workflowRequest
        ?.requestSections ?? []) as RequestSectionRow[];
      if (!workflow?.sections?.length) {
        return { sectionMappingsByRequestSectionUuid: map, sectionMappingsByStepIndex: byStep };
      }

      // Resolver order matches workflow_section.position; keep array index for REST fallback.
      for (const rs of requestSections) {
        const templateSectionId = rs.workflowSection?.id;
        let section = templateSectionId
          ? workflow.sections.find(
              (s) => String(s.id) === String(templateSectionId)
            )
          : undefined;
        // If GraphQL omits workflowSection.id (older schema) or ids differ, match by title.
        if (!section && rs.title) {
          const t = String(rs.title).trim();
          section = workflow.sections.find(
            (s) => String(s.title || '').trim() === t
          );
        }
        if (!section) continue;
        const sortedItems = [...(section.items2 || [])].sort(
          (a, b) => (a.position ?? 0) - (b.position ?? 0)
        );
        const mapping: WorkflowSectionMapping = {
          workflowSectionId: section.id,
          workflowItemIds: sortedItems.map((item) => item.id),
        };
        map.set(normalizeRequestSectionKey(String(rs.id)), mapping);
        byStep.push(mapping);
      }
      return {
        sectionMappingsByRequestSectionUuid: map,
        sectionMappingsByStepIndex: byStep,
      };
    }, [workflowRequestData]);

  const getWorkflowSectionMapping = useCallback(
    (requestSectionUuid: string | undefined, stepIndex: number) => {
      if (requestSectionUuid) {
        const byUuid = sectionMappingsByRequestSectionUuid.get(
          normalizeRequestSectionKey(requestSectionUuid)
        );
        if (byUuid) return byUuid;
      }
      return sectionMappingsByStepIndex[stepIndex];
    },
    [sectionMappingsByRequestSectionUuid, sectionMappingsByStepIndex]
  );

  const {
    firmAuthoredContentByRequestItemUuid,
    firmAuthoredContentByWorkflowItemId,
    isCustomStepByRequestItemUuid,
    isCustomStepByWorkflowItemId,
  } = useMemo(() => {
    const m = new Map<string, FirmAuthoredContentPayload>();
    const byWorkflowItemId = new Map<string, FirmAuthoredContentPayload>();
    const customMap = new Map<string, boolean>();
    const customByWorkflowItemId = new Map<string, boolean>();
    const sections = workflowRequestData?.workflowRequest?.workflow?.sections;
    if (!sections) {
      return {
        firmAuthoredContentByRequestItemUuid: m,
        firmAuthoredContentByWorkflowItemId: byWorkflowItemId,
        isCustomStepByRequestItemUuid: customMap,
        isCustomStepByWorkflowItemId: customByWorkflowItemId,
      };
    }

    for (const sec of sections) {
      for (const wiUntyped of sec.items2 ?? []) {
        const wi = wiUntyped as WorkflowItemRow;
        const rid = wi.requestItem?.id;
        const wiKey = normalizeRequestSectionKey(String(wi.id));

        if (isWorkflowCustomStep(wi.options)) {
          customByWorkflowItemId.set(wiKey, true);
        }

        if (rid) {
          const key = normalizeRequestSectionKey(String(rid));
          if (isWorkflowCustomStep(wi.options)) {
            customMap.set(key, true);
          }
        }

        const rawFacObj = getFirmAuthoredContentRawFromOptions(wi.options);
        const useSyntheticFac = isWorkflowCustomStep(wi.options);
        const facSource: unknown =
          rawFacObj ??
          (useSyntheticFac
            ? syntheticFirmAuthoredForApproveFallback(wi.type)
            : null);

        if (!facSource || typeof facSource !== 'object') {
          continue;
        }
        const normalized = normalizeFirmAuthoredContentFromOptions(facSource);
        if (!normalized) continue;
        const merged = enrichFirmAuthoredContentWithDocumentDependency(
          normalized,
          wi.requestItem?.dependency
        );
        byWorkflowItemId.set(wiKey, merged);
        if (rid) {
          m.set(normalizeRequestSectionKey(String(rid)), merged);
        }
      }
    }
    return {
      firmAuthoredContentByRequestItemUuid: m,
      firmAuthoredContentByWorkflowItemId: byWorkflowItemId,
      isCustomStepByRequestItemUuid: customMap,
      isCustomStepByWorkflowItemId: customByWorkflowItemId,
    };
  }, [workflowRequestData]);

  const { mutateAsync: reorderMutationAsync, isLoading: isReordering } =
    gqlClient.useMutationGraphQL(reorderWorkflowItemsMutationDocument, {
      onSuccess: () => {
        queryClient.invalidateQueries(['workflowRequest']);
        queryClient.invalidateQueries(['items']);
      },
    });

  const reorderItemsForRequestSection = useCallback(
    async (
      requestSectionUuid: string,
      newItemIds: string[],
      stepIndex?: number
    ) => {
      const mapping =
        sectionMappingsByRequestSectionUuid.get(
          normalizeRequestSectionKey(requestSectionUuid)
        ) ??
        (stepIndex !== undefined
          ? sectionMappingsByStepIndex[stepIndex]
          : undefined);
      if (!mapping) {
        return;
      }

      await reorderMutationAsync({
        workflowSectionId: mapping.workflowSectionId,
        workflowItemIds: newItemIds,
      });
    },
    [
      sectionMappingsByRequestSectionUuid,
      sectionMappingsByStepIndex,
      reorderMutationAsync,
    ]
  );

  return {
    sectionMappingsByRequestSectionUuid,
    sectionMappingsByStepIndex,
    getWorkflowSectionMapping,
    firmAuthoredContentByRequestItemUuid,
    firmAuthoredContentByWorkflowItemId,
    isCustomStepByRequestItemUuid,
    isCustomStepByWorkflowItemId,
    reorderItemsForRequestSection,
    workflowId,
    isWorkflowLoading,
    isReordering,
  };
}
